import type {
  AdminBillingSettingsResponse,
  AdminBillingSettingsUpdateInput,
  AdminBillingOrderListResponse,
  AdminBillingOrderQuery,
  AdminAuditListResponse,
  AdminAuditQuery,
  AdminPublicCollectProxyPoolInput,
  AdminPublicCollectProxyPoolResponse,
  AdminBillingEventListResponse,
  AdminDesktopUpdateReleaseInput,
  AdminDesktopUpdateReleaseListQuery,
  AdminDesktopUpdateReleaseListResponse,
  AdminDesktopUpdateReleaseUploadInput,
  AdminDesktopUpdateReleaseUpdateInput,
  AdminDesktopUpdateReleaseView,
  AdminLicenseInput,
  AdminOverview,
  AdminRuleBundlePublishInput,
  AdminRuleBundleView,
  AdminSession,
  AdminSyncDeviceRevokeResponse,
  AdminTenantCreateInput,
  AdminTenantDetailResponse,
  AdminTenantProfileInput,
  AdminTenantSyncOverviewResponse,
  DeviceView,
  RuleBundlePlatform,
  TenantLicenseView
} from "@shudian/contracts";
import { request } from "./http.js";

export function adminLogin(input: { email: string; password: string }): Promise<AdminSession> {
  return request<AdminSession>("/api/admin/auth/login", {
    method: "POST",
    body: input,
    withAdminAuth: false
  });
}

export function adminLogout(): Promise<{ ok: true }> {
  return request<{ ok: true }>("/api/admin/auth/logout", { method: "POST" });
}

export function adminGetMe(): Promise<{ admin: AdminSession["admin"] }> {
  return request("/api/admin/me");
}

export function adminGetOverview(): Promise<AdminOverview> {
  return request<AdminOverview>("/api/admin/overview");
}

export function adminGetTenant(tenantId: string): Promise<AdminTenantDetailResponse> {
  return request<AdminTenantDetailResponse>(
    `/api/admin/tenants/${encodeURIComponent(tenantId)}`
  );
}

export function adminCreateTenant(
  input: AdminTenantCreateInput
): Promise<AdminTenantDetailResponse> {
  return request<AdminTenantDetailResponse>("/api/admin/tenants", {
    method: "POST",
    body: input
  });
}

export function adminUpdateTenantProfile(
  tenantId: string,
  input: AdminTenantProfileInput
): Promise<AdminTenantDetailResponse> {
  return request<AdminTenantDetailResponse>(
    `/api/admin/tenants/${encodeURIComponent(tenantId)}/profile`,
    { method: "PUT", body: input }
  );
}

export function adminUpdateTenantLicense(
  tenantId: string,
  input: AdminLicenseInput
): Promise<{ license: TenantLicenseView }> {
  return request<{ license: TenantLicenseView }>(
    `/api/admin/tenants/${encodeURIComponent(tenantId)}/license`,
    { method: "PUT", body: input }
  );
}

export function adminResetTenantOwnerPassword(
  tenantId: string,
  input: { password: string }
): Promise<{ ownerEmail: string }> {
  return request<{ ownerEmail: string }>(
    `/api/admin/tenants/${encodeURIComponent(tenantId)}/owner/reset-password`,
    { method: "POST", body: input }
  );
}

export function adminRevokeTenantDevice(
  tenantId: string,
  deviceId: string
): Promise<{ device: DeviceView }> {
  return request<{ device: DeviceView }>(
    `/api/admin/tenants/${encodeURIComponent(tenantId)}/devices/revoke`,
    { method: "POST", body: { deviceId } }
  );
}

export function adminGetTenantSync(tenantId: string): Promise<AdminTenantSyncOverviewResponse> {
  return request<AdminTenantSyncOverviewResponse>(
    `/api/admin/tenants/${encodeURIComponent(tenantId)}/sync`
  );
}

export function adminRevokeTenantSyncDevice(
  tenantId: string,
  deviceId: string
): Promise<AdminSyncDeviceRevokeResponse> {
  return request<AdminSyncDeviceRevokeResponse>(
    `/api/admin/tenants/${encodeURIComponent(tenantId)}/sync/devices/${encodeURIComponent(deviceId)}/revoke`,
    { method: "POST" }
  );
}

export function adminGetPublicCollectProxyPool(): Promise<AdminPublicCollectProxyPoolResponse> {
  return request<AdminPublicCollectProxyPoolResponse>("/api/admin/system/public-collect-proxy-pool");
}

export function adminSavePublicCollectProxyPool(
  input: AdminPublicCollectProxyPoolInput
): Promise<AdminPublicCollectProxyPoolResponse> {
  return request<AdminPublicCollectProxyPoolResponse>(
    "/api/admin/system/public-collect-proxy-pool",
    { method: "PUT", body: input }
  );
}

export function adminGetLatestRuleBundle(
  platform: RuleBundlePlatform = "ruten"
): Promise<AdminRuleBundleView> {
  return request<AdminRuleBundleView>("/api/admin/rule-bundles/latest", {
    search: { platform }
  });
}

export function adminPublishRuleBundle(
  input: AdminRuleBundlePublishInput
): Promise<AdminRuleBundleView> {
  return request<AdminRuleBundleView>("/api/admin/rule-bundles", {
    method: "POST",
    body: input
  });
}

export function adminListAudit(
  query: AdminAuditQuery
): Promise<AdminAuditListResponse> {
  return request<AdminAuditListResponse>("/api/admin/audit", {
    search: {
      tenantId: query.tenantId,
      action: query.action,
      limit: query.limit
    }
  });
}

export function adminListBillingOrders(
  query: AdminBillingOrderQuery = {}
): Promise<AdminBillingOrderListResponse> {
  return request<AdminBillingOrderListResponse>("/api/admin/billing/orders", {
    search: {
      tenantId: query.tenantId,
      status: query.status,
      limit: query.limit
    }
  });
}

export function adminGetBillingSettings(): Promise<AdminBillingSettingsResponse> {
  return request<AdminBillingSettingsResponse>("/api/admin/billing/settings");
}

export function adminUpdateBillingSettings(
  input: AdminBillingSettingsUpdateInput
): Promise<AdminBillingSettingsResponse> {
  return request<AdminBillingSettingsResponse>("/api/admin/billing/settings", {
    method: "PUT",
    body: input
  });
}

export function adminListTenantBillingOrders(
  tenantId: string,
  query: Omit<AdminBillingOrderQuery, "tenantId"> = {}
): Promise<AdminBillingOrderListResponse> {
  return request<AdminBillingOrderListResponse>(
    `/api/admin/tenants/${encodeURIComponent(tenantId)}/billing-orders`,
    {
      search: {
        status: query.status,
        limit: query.limit
      }
    }
  );
}

export function adminListDesktopUpdateReleases(
  query: AdminDesktopUpdateReleaseListQuery = {}
): Promise<AdminDesktopUpdateReleaseListResponse> {
  return request<AdminDesktopUpdateReleaseListResponse>("/api/admin/desktop-updates", {
    search: {
      target: query.target,
      arch: query.arch,
      channel: query.channel,
      includeDisabled: query.includeDisabled,
      limit: query.limit
    }
  });
}

export function adminPublishDesktopUpdateRelease(
  input: AdminDesktopUpdateReleaseInput
): Promise<{ release: AdminDesktopUpdateReleaseView }> {
  return request<{ release: AdminDesktopUpdateReleaseView }>("/api/admin/desktop-updates", {
    method: "POST",
    body: input
  });
}

export function adminUploadDesktopUpdateRelease(input: {
  metadata: AdminDesktopUpdateReleaseUploadInput;
  artifact: File;
  signature: File;
}): Promise<{ release: AdminDesktopUpdateReleaseView }> {
  const formData = new FormData();
  for (const [key, value] of Object.entries(input.metadata)) {
    if (value === undefined || value === "") continue;
    formData.set(key, String(value));
  }
  formData.set("artifact", input.artifact);
  formData.set("signature", input.signature);
  return request<{ release: AdminDesktopUpdateReleaseView }>(
    "/api/admin/desktop-updates/upload",
    {
      method: "POST",
      formData
    }
  );
}

export function adminUpdateDesktopUpdateRelease(
  releaseId: string,
  input: AdminDesktopUpdateReleaseUpdateInput
): Promise<{ release: AdminDesktopUpdateReleaseView }> {
  return request<{ release: AdminDesktopUpdateReleaseView }>(
    `/api/admin/desktop-updates/${encodeURIComponent(releaseId)}`,
    { method: "PUT", body: input }
  );
}

export function adminEnableDesktopUpdateRelease(
  releaseId: string
): Promise<{ release: AdminDesktopUpdateReleaseView }> {
  return request<{ release: AdminDesktopUpdateReleaseView }>(
    `/api/admin/desktop-updates/${encodeURIComponent(releaseId)}/enable`,
    { method: "POST" }
  );
}

export function adminDisableDesktopUpdateRelease(
  releaseId: string
): Promise<{ release: AdminDesktopUpdateReleaseView }> {
  return request<{ release: AdminDesktopUpdateReleaseView }>(
    `/api/admin/desktop-updates/${encodeURIComponent(releaseId)}/disable`,
    { method: "POST" }
  );
}

export function adminListTenantBillingEvents(
  tenantId: string
): Promise<AdminBillingEventListResponse> {
  return request<AdminBillingEventListResponse>(
    `/api/admin/tenants/${encodeURIComponent(tenantId)}/billing-events`
  );
}
